/**
 * UI-permission model (design §14).
 *
 * Action/resource strings are the ones the **authorization service actually
 * evaluates**, i.e. the `@RequirePolicy(action, resource)` decorators on the
 * routes behind each affordance (verified in the backend controllers). Where
 * design §14.6 PROPOSED a string, the server's decorator wins:
 *
 * | Affordance        | design (P1-T1)              | server decorator (source of truth)        |
 * |-------------------|-----------------------------|-------------------------------------------|
 * | Add member        | `organizations:member:add`  | same, on `org:${id}`                       |
 * | Remove member     | `organizations:member:remove`| same, on `org:${id}`                      |
 * | New project       | `projects:create` @ `org:${id}` | `projects:create` @ `project:*`       |
 * | Add resource      | `projects:resource:add`     | `projects:attach` @ `project:*`            |
 * | Delete resource   | `projects:resource:remove`  | `projects:detach` @ `project:*`            |
 * | Assign/unassign/
 * |  renew role       | (not specified)             | `roles:assign` @ `role:${id}`              |
 *
 * The resource string sent to `POST /policies/evaluate/self` must equal the string
 * the server will evaluate for that call, otherwise a policy written for
 * `project:*` would not match the client's `project:123` probe and the UI would
 * hide a permitted action. Wildcards in the *policy* document are matched
 * against this string by the server (`policy.service.ts` glob matcher).
 *
 * `can()` is a UX affordance only — every mutation stays server-enforced (P4).
 */

export const PERMISSION_ACTIONS = {
  /** `@RequirePolicy('organizations:member:add', 'org:${id}')` */
  memberAdd: 'organizations:member:add',
  /** `@RequirePolicy('organizations:member:remove', 'org:${id}')` */
  memberRemove: 'organizations:member:remove',
  /** `@RequirePolicy('projects:create', 'project:*')` */
  projectCreate: 'projects:create',
  /** `@RequirePolicy('projects:attach', 'project:*')` */
  resourceAdd: 'projects:attach',
  /** `@RequirePolicy('projects:detach', 'project:*')` */
  resourceRemove: 'projects:detach',
  /** `@RequirePolicy('roles:assign', 'role:${id}')` */
  roleAssign: 'roles:assign',
} as const;

export type PermissionAction = (typeof PERMISSION_ACTIONS)[keyof typeof PERMISSION_ACTIONS];

/** `org:${orgId}` — CONFIRMED format (the organizations guard interpolates `:id`). */
export const orgResource = (orgId: string) => `org:${orgId}`;
/** `role:${roleId}` — CONFIRMED format (the roles guard interpolates `:id`). */
export const roleResource = (roleId: string) => `role:${roleId}`;
/** Projects are guarded on the literal `project:*` resource (no id interpolation). */
export const PROJECT_RESOURCE = 'project:*';

/** Cache lifetime for a resolved decision (design §14.4). */
export const PERMISSION_TTL_MS = 300_000;
/** A rate-limited denial is transient — retry much sooner (design §14.1). */
export const PERMISSION_RATE_LIMITED_TTL_MS = 10_000;

/** Key for the RTK Query tag so a mutation can invalidate every decision about one resource. */
export const PERMISSION_TAG = 'Permission' as const;

export interface PolicyEvaluateRequest {
  userId: string;
  action: string;
  resource: string;
  context?: Record<string, unknown>;
  /** Never sent by workspace: `policyIds` scoping is for API-key sessions only. */
  policyIds?: string[];
}

export interface PolicyRateLimit {
  [key: string]: unknown;
}

export interface PolicyEvaluateResult {
  allowed: boolean;
  rateLimit?: PolicyRateLimit;
}
